<template>
  <div
    class="circular-loading"
    :style="componentStyle"
  >
    <svg v-if="showLoading || showPath" class="circular-loading-svg" viewBox="25 25 50 50">
      <circle
        v-if="showLoading"
        class="circular-loading-track"
        cx="50"
        cy="50"
        r="22"
        fill="none"
      />
      <circle
        v-if="showPath"
        :class="['circular-loading-path', { 'circular-loading-path-full': showCircle }]"
        cx="50"
        cy="50"
        r="22"
        fill="none"
      />
    </svg>

    <div class="circular-loading-center">
      <slot></slot>
    </div>
  </div>
</template>

<script>
export default {
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    size: {
      type: [Number, String],
      default: 50
    },
    color: {
      type: String,
      default: '#ADB5C3'
    },
    trackColor: {
      type: String,
      default: '#ECECEE'
    },
    showCircle: {
      type: Boolean,
      default: false
    },
    contentGap: {
      type: [Number, String],
      default: 0
    },
    strokeWidth: {
      type: [Number, String],
      default: 3
    },
    duration: {
      type: [Number, String],
      default: 1
    },
    iconSize: {
      type: [Number, String],
      default: null
    }
  },
  computed: {
    componentStyle () {
      const size = this.normalizeSize(this.size)
      const strokeWidth = this.normalizeSize(this.strokeWidth)
      const contentGap = this.normalizeSize(this.contentGap)
      const duration = this.normalizeDuration(this.duration)
      const iconSize = this.iconSize ? this.normalizeSize(this.iconSize) : `${Math.round(Number.parseFloat(size) * 0.35)}px`

      return {
        '--circular-loading-size': size,
        '--circular-loading-color': this.color || '#adb5c3',
        '--circular-loading-track-color': this.trackColor || '#ececec',
        '--circular-loading-stroke-width': strokeWidth,
        '--circular-loading-content-gap': contentGap,
        '--circular-loading-duration': duration,
        '--circular-loading-icon-size': iconSize
      }
    },
    showLoading () {
      return this.value
    },
    showPath () {
      return this.showLoading || this.showCircle
    }
  },
  methods: {
    normalizeSize (value) {
      return typeof value === 'number' ? `${value}px` : value
    },
    normalizeDuration (value) {
      return typeof value === 'number' ? `${value}s` : value
    }
  }
}
</script>

<style scoped lang="scss">
.circular-loading {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: calc(var(--circular-loading-size) + var(--circular-loading-content-gap));
  height: calc(var(--circular-loading-size) + var(--circular-loading-content-gap));

  .circular-loading-svg {
    position: absolute;
    inset: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    pointer-events: none;
    transform: rotate(-90deg);
  }

  .circular-loading-path {
    stroke: var(--circular-loading-color);
    stroke-width: var(--circular-loading-stroke-width);
    stroke-linecap: round;
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
    animation: circular-loading-dash var(--circular-loading-duration) linear infinite;

    &.circular-loading-path-full {
      stroke-dasharray: none;
      stroke-dashoffset: 0;
      animation: none;
    }
  }

  .circular-loading-track {
    stroke: var(--circular-loading-track-color);
    stroke-width: var(--circular-loading-stroke-width);
  }

  .circular-loading-center {
    position: absolute;
    inset: calc(var(--circular-loading-stroke-width) + var(--circular-loading-content-gap));
    z-index: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--circular-loading-color);
    font-size: var(--circular-loading-icon-size);
    line-height: 1;

    > * {
      max-width: 100%;
      max-height: 100%;
    }
  }
}

@keyframes circular-loading-dash {
  0% {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
  }

  25% {
    stroke-dasharray: 30, 200;
    stroke-dashoffset: -10px;
  }

  50% {
    stroke-dasharray: 60, 200;
    stroke-dashoffset: -30px;
  }

  75% {
    stroke-dasharray: 30, 200;
    stroke-dashoffset: -100px;
  }

  100% {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: -140px;
  }
}
</style>
